import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Rocket, Users, DollarSign, TrendingUp, Clock, Target,
  Mail, Phone, Globe, Calendar, ChevronRight, RefreshCw,
  CheckCircle2, AlertCircle, Eye, X, Building, Award, Send, Bell
} from "lucide-react";

// Status configuration
const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-500", textColor: "text-blue-700", bgColor: "bg-blue-50" },
  reviewing: { label: "Reviewing", color: "bg-amber-500", textColor: "text-amber-700", bgColor: "bg-amber-50" },
  qualified: { label: "Qualified", color: "bg-green-500", textColor: "text-green-700", bgColor: "bg-green-50" },
  scheduled: { label: "Scheduled", color: "bg-purple-500", textColor: "text-purple-700", bgColor: "bg-purple-50" },
  converted: { label: "Converted", color: "bg-emerald-600", textColor: "text-emerald-700", bgColor: "bg-emerald-50" },
  declined: { label: "Declined", color: "bg-red-500", textColor: "text-red-700", bgColor: "bg-red-50" }
};

// Revenue mapping for display
const REVENUE_MAP = {
  "25k_50k": "$25K-$50K/mo",
  "50k_100k": "$50K-$100K/mo",
  "100k_250k": "$100K-$250K/mo",
  "250k_plus": "$250K+/mo"
};

const TEAM_MAP = {
  "solo": "Solo",
  "2_5": "2-5",
  "6_15": "6-15",
  "15_plus": "15+"
};

const CHALLENGE_MAP = {
  delegation: "Delegation",
  systems: "Systems",
  leadership: "Leadership",
  strategy: "Strategy",
  operations: "Operations",
  sales: "Sales"
};

export const ScaleApplicationsPanel = ({ isOpen, onClose }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusNotes, setStatusNotes] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sendEmailOnUpdate, setSendEmailOnUpdate] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchApplications();
    }
  }, [isOpen]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/consultant/scale-applications`);
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await authFetch(`${API}/consultant/scale-applications/${applicationId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          status: newStatus, 
          notes: statusNotes,
          send_email: sendEmailOnUpdate 
        })
      });
      
      if (response.ok) {
        const emailMsg = sendEmailOnUpdate && newStatus !== "new" && newStatus !== "declined" 
          ? " (email sent to applicant)" 
          : "";
        toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}${emailMsg}`);
        setApplications(prev => prev.map(app => 
          app.application_id === applicationId ? { ...app, status: newStatus } : app
        ));
        setSelectedApp(null);
        setStatusNotes("");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getQualificationBadge = (app) => {
    // Calculate score based on same logic as backend
    let score = 0;
    if (["100k_250k", "250k_plus"].includes(app.monthly_revenue)) score += 30;
    else if (app.monthly_revenue === "50k_100k") score += 20;
    else score += 10;
    
    if (["6_15", "15_plus"].includes(app.team_size)) score += 20;
    else if (app.team_size === "2_5") score += 15;
    
    if (app.investment_ready === "yes") score += 25;
    else if (app.investment_ready === "stretch") score += 15;
    
    if (app.time_commitment === "yes") score += 15;
    else if (app.time_commitment === "mostly") score += 10;
    
    if (["5_10_years", "10_plus"].includes(app.years_in_business)) score += 10;

    if (score >= 80) return { label: "Highly Qualified", color: "bg-green-100 text-green-700", icon: "🟢" };
    if (score >= 60) return { label: "Good Fit", color: "bg-amber-100 text-amber-700", icon: "🟡" };
    return { label: "Needs Review", color: "bg-orange-100 text-orange-700", icon: "🟠" };
  };

  const filteredApplications = filterStatus === "all" 
    ? applications 
    : applications.filter(app => app.status === filterStatus);

  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === "new").length,
    qualified: applications.filter(a => a.status === "qualified").length,
    converted: applications.filter(a => a.status === "converted").length
  };

  // Calculate applications pending 48+ hours
  const pendingOver48hrs = applications.filter(a => {
    if (a.status !== "new") return false;
    const created = new Date(a.created_at);
    const now = new Date();
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    return hoursDiff >= 48;
  }).length;

  const checkReminders = async () => {
    try {
      const response = await authFetch(`${API}/consultant/scale-applications/check-reminders`, {
        method: "POST"
      });
      if (response.ok) {
        const data = await response.json();
        if (data.applications_count > 0) {
          toast.success(`Reminder sent for ${data.applications_count} application(s)`);
        } else {
          toast.info("No applications need reminders right now");
        }
      }
    } catch (error) {
      toast.error("Failed to check reminders");
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-amber-500" />
            Scale & Leadership Applications
          </DialogTitle>
        </DialogHeader>

        {/* Stats Bar */}
        <div className="grid grid-cols-5 gap-4 py-4 border-b">
          <div className="text-center">
            <p className="text-2xl font-bold text-neutral-900">{stats.total}</p>
            <p className="text-xs text-neutral-500">Total</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
            <p className="text-xs text-neutral-500">New</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{stats.qualified}</p>
            <p className="text-xs text-neutral-500">Qualified</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.converted}</p>
            <p className="text-xs text-neutral-500">Converted</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${pendingOver48hrs > 0 ? 'text-red-600' : 'text-neutral-400'}`}>
              {pendingOver48hrs}
            </p>
            <p className="text-xs text-neutral-500">48h+ Pending</p>
          </div>
        </div>

        {/* 48-hour warning banner */}
        {pendingOver48hrs > 0 && (
          <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-600" />
              <span className="text-sm text-red-700">
                <strong>{pendingOver48hrs}</strong> application{pendingOver48hrs > 1 ? 's have' : ' has'} been waiting 48+ hours
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkReminders}
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              <Bell className="w-4 h-4 mr-2" />
              Send Reminder
            </Button>
          </div>
        )}

        {/* Filter & Refresh */}
        <div className="flex items-center justify-between py-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Applications</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchApplications} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Applications List */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-neutral-400 mx-auto mb-3" />
              <p className="text-neutral-500">Loading applications...</p>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <Rocket className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No applications found</p>
              <p className="text-sm text-neutral-400">
                {filterStatus !== "all" ? "Try changing the filter" : "Applications will appear here"}
              </p>
            </div>
          ) : (
            filteredApplications.map((app) => {
              const qualification = getQualificationBadge(app);
              const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.new;
              
              return (
                <Card 
                  key={app.application_id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedApp(app)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900">{app.name}</h3>
                          <Badge className={qualification.color}>
                            {qualification.icon} {qualification.label}
                          </Badge>
                          <Badge className={`${statusConfig.bgColor} ${statusConfig.textColor}`}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-neutral-600 mb-2">{app.business_name} • {app.industry}</p>
                        <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {REVENUE_MAP[app.monthly_revenue] || app.monthly_revenue}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {TEAM_MAP[app.team_size] || app.team_size} people
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {CHALLENGE_MAP[app.primary_challenge] || app.primary_challenge}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(app.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Application Detail Modal */}
        {selectedApp && (
          <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  {selectedApp.name} - {selectedApp.business_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Qualification & Status */}
                <div className="flex items-center gap-3">
                  {(() => {
                    const q = getQualificationBadge(selectedApp);
                    return <Badge className={`${q.color} text-sm`}>{q.icon} {q.label}</Badge>;
                  })()}
                  <Badge className={`${STATUS_CONFIG[selectedApp.status]?.bgColor} ${STATUS_CONFIG[selectedApp.status]?.textColor}`}>
                    {STATUS_CONFIG[selectedApp.status]?.label}
                  </Badge>
                </div>

                {/* Contact Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-neutral-400" />
                      <a href={`mailto:${selectedApp.email}`} className="text-blue-600 hover:underline">
                        {selectedApp.email}
                      </a>
                    </div>
                    {selectedApp.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-neutral-400" />
                        <span>{selectedApp.phone}</span>
                      </div>
                    )}
                    {selectedApp.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-neutral-400" />
                        <a href={selectedApp.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          {selectedApp.website}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Business Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Business Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500">Industry</p>
                        <p className="font-medium">{selectedApp.industry}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Years in Business</p>
                        <p className="font-medium">{selectedApp.years_in_business?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Monthly Revenue</p>
                        <p className="font-medium text-green-600">{REVENUE_MAP[selectedApp.monthly_revenue]}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Team Size</p>
                        <p className="font-medium">{TEAM_MAP[selectedApp.team_size]} people</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Revenue Goal</p>
                        <p className="font-medium">{selectedApp.revenue_goal?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Hiring Plans</p>
                        <p className="font-medium">{selectedApp.hiring_plans?.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Challenge & Goals */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Challenge & Goals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div>
                      <p className="text-neutral-500 mb-1">Primary Challenge</p>
                      <Badge variant="outline" className="text-red-600 border-red-200">
                        {CHALLENGE_MAP[selectedApp.primary_challenge] || selectedApp.primary_challenge}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-neutral-500 mb-1">Challenge Details</p>
                      <p className="bg-neutral-50 p-3 rounded-lg">{selectedApp.challenge_details}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500 mb-1">Why Now</p>
                      <p className="bg-neutral-50 p-3 rounded-lg">{selectedApp.why_now}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500 mb-1">Ideal 6-Month Outcome</p>
                      <p className="bg-neutral-50 p-3 rounded-lg">{selectedApp.ideal_outcome}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Commitment */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Commitment Level</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-neutral-500">Investment Ready</p>
                        <p className="font-medium">{selectedApp.investment_ready?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <p className="text-neutral-500">Weekly Sessions</p>
                        <p className="font-medium">{selectedApp.time_commitment?.replace(/_/g, " ")}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-neutral-500">Previous Support</p>
                        <p className="font-medium">
                          {selectedApp.previous_support?.length > 0 
                            ? selectedApp.previous_support.join(", ") 
                            : "None selected"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Status Update */}
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Award className="w-4 h-4 text-amber-500" />
                      Update Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Textarea
                        value={statusNotes}
                        onChange={(e) => setStatusNotes(e.target.value)}
                        placeholder="Add any notes about this application..."
                        className="bg-white"
                      />
                    </div>
                    
                    {/* Email notification toggle */}
                    <div className="flex items-center space-x-2 p-3 bg-white rounded-lg border">
                      <Checkbox
                        id="send-email"
                        checked={sendEmailOnUpdate}
                        onCheckedChange={setSendEmailOnUpdate}
                      />
                      <div className="flex-1">
                        <Label htmlFor="send-email" className="cursor-pointer flex items-center gap-2">
                          <Send className="w-4 h-4 text-amber-500" />
                          Send email notification to applicant
                        </Label>
                        <p className="text-xs text-neutral-500 mt-1">
                          Automatically notify {selectedApp.name?.split(" ")[0]} when status changes
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <Button
                          key={key}
                          size="sm"
                          variant={selectedApp.status === key ? "default" : "outline"}
                          onClick={() => updateStatus(selectedApp.application_id, key)}
                          disabled={updatingStatus || selectedApp.status === key}
                          className={selectedApp.status === key ? config.color : ""}
                        >
                          {config.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedApp(null)}>
                  Close
                </Button>
                <Button asChild className="bg-amber-600 hover:bg-amber-700">
                  <a href={`mailto:${selectedApp.email}?subject=Re: Your Scale & Leadership Application`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email Applicant
                  </a>
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScaleApplicationsPanel;
